import { initContract } from '@ts-rest/core';
import { z } from 'zod';

import { docListObjectSchema } from './docs.ts';

const c = initContract();

const errorSchema = z.object({ message: z.string() });
const prosemirrorJsonSchema = z.record(z.string(), z.unknown());

const projectIdSchema = z.string().regex(/^proj_[0-9A-Za-z]{22}$/);

export const projectStatusValues = ['active', 'archived'] as const;
export const projectStatusSchema = z.enum(projectStatusValues);

export const projectAuthorSchema = z.object({
  id: z.string().min(1),
  display_name: z.string().min(1),
});

export const projectThreadSchema = z.object({
  thread_id: z.string().min(1),
  title: z.string().min(1),
  added_at: z.string().datetime(),
  added_by: projectAuthorSchema,
});

export const projectListObjectSchema = z.object({
  id: projectIdSchema,
  name: z.string().min(1),
  status: projectStatusSchema,
  author: projectAuthorSchema,
  thread_count: z.number().int().nonnegative(),
  doc_count: z.number().int().nonnegative(),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
  archived_at: z.string().datetime().nullable(),
});

export const projectListResponseSchema = z.object({
  type: z.literal('list'),
  list_type: z.literal('project'),
  objects: z.array(projectListObjectSchema),
});

export const listProjectsQuerySchema = z.object({
  status: projectStatusSchema.optional(),
});

export const createProjectRequestSchema = z.object({
  name: z.string().trim().min(1).max(200),
  prosemirror_json: prosemirrorJsonSchema.optional(),
});

export const createProjectResponseSchema = z.object({
  id: projectIdSchema,
  created_at: z.string().datetime(),
});

export const updateProjectRequestSchema = z
  .object({
    name: z.string().trim().min(1).max(200).optional(),
    prosemirror_json: prosemirrorJsonSchema.optional(),
  })
  .refine((body) => Object.keys(body).length > 0, {
    message: 'At least one field must be provided',
  });

export const updateProjectResponseSchema = z.object({
  id: projectIdSchema,
  updated_at: z.string().datetime(),
});

export const archiveProjectRequestSchema = z.object({
  archived: z.boolean(),
});

export const archiveProjectResponseSchema = z.object({
  id: projectIdSchema,
  status: projectStatusSchema,
  archived_at: z.string().datetime().nullable(),
});

export const getProjectResponseSchema = z.object({
  id: projectIdSchema,
  name: z.string().min(1),
  prosemirror_json: prosemirrorJsonSchema,
  status: projectStatusSchema,
  author: projectAuthorSchema,
  // One-paragraph AI synthesis of what's happening across the project's
  // threads. Null until the first synthesis runs; refreshed on demand
  // via POST /projects/:id/summary.
  summary: z.string().nullable(),
  summary_at: z.string().datetime().nullable(),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
  archived_at: z.string().datetime().nullable(),
  threads: z.array(projectThreadSchema),
  docs: z.array(docListObjectSchema),
});

export const synthesizeProjectResponseSchema = z.object({
  id: projectIdSchema,
  summary: z.string(),
  summary_at: z.string().datetime(),
});

export const addProjectThreadRequestSchema = z.object({
  thread_id: z.string().min(1).max(64),
});

export const addProjectThreadResponseSchema = projectThreadSchema;

export const threadProjectSchema = z.object({
  id: projectIdSchema,
  name: z.string().min(1),
  status: projectStatusSchema,
});

export const listThreadProjectsResponseSchema = z.object({
  type: z.literal('list'),
  list_type: z.literal('thread_project'),
  objects: z.array(threadProjectSchema),
});

export const projectsContract = c.router({
  listProjects: {
    method: 'GET',
    path: '/projects',
    headers: z.object({
      authorization: z.string().min(1).optional(),
    }),
    query: listProjectsQuerySchema,
    responses: {
      200: projectListResponseSchema,
      401: errorSchema,
    },
    summary: 'List projects in the authenticated user’s organization',
  },
  createProject: {
    method: 'POST',
    path: '/projects',
    headers: z.object({
      authorization: z.string().min(1).optional(),
    }),
    body: createProjectRequestSchema,
    responses: {
      201: createProjectResponseSchema,
      401: errorSchema,
      422: errorSchema,
    },
    summary: 'Create a new project in the authenticated user’s organization',
  },
  getProject: {
    method: 'GET',
    path: '/projects/:projectId',
    pathParams: z.object({
      projectId: projectIdSchema,
    }),
    headers: z.object({
      authorization: z.string().min(1).optional(),
    }),
    responses: {
      200: getProjectResponseSchema,
      401: errorSchema,
      403: errorSchema,
      404: errorSchema,
    },
    summary:
      'Get a project with its attached threads and child docs. The project description is the canonical mention source for project-scoped @-mentions.',
  },
  updateProject: {
    method: 'PATCH',
    path: '/projects/:projectId',
    pathParams: z.object({
      projectId: projectIdSchema,
    }),
    headers: z.object({
      authorization: z.string().min(1).optional(),
    }),
    body: updateProjectRequestSchema,
    responses: {
      200: updateProjectResponseSchema,
      401: errorSchema,
      403: errorSchema,
      404: errorSchema,
      422: errorSchema,
    },
    summary:
      'Update a project’s name or rich-text body. Body changes re-sync mention_references for source_kind=project.',
  },
  archiveProject: {
    method: 'POST',
    path: '/projects/:projectId/archive',
    pathParams: z.object({
      projectId: projectIdSchema,
    }),
    headers: z.object({
      authorization: z.string().min(1).optional(),
    }),
    body: archiveProjectRequestSchema,
    responses: {
      200: archiveProjectResponseSchema,
      401: errorSchema,
      403: errorSchema,
      404: errorSchema,
    },
    summary: 'Archive (soft-delete) or unarchive a project',
  },
  synthesizeProject: {
    method: 'POST',
    path: '/projects/:projectId/summary',
    pathParams: z.object({
      projectId: projectIdSchema,
    }),
    headers: z.object({
      authorization: z.string().min(1).optional(),
    }),
    body: z.object({}).optional(),
    responses: {
      200: synthesizeProjectResponseSchema,
      401: errorSchema,
      403: errorSchema,
      404: errorSchema,
      409: errorSchema,
      503: errorSchema,
    },
    summary:
      'Generate (or refresh) the project’s one-paragraph summary from its pinned threads. Persists to projects.summary.',
  },
  addProjectThread: {
    method: 'POST',
    path: '/projects/:projectId/threads',
    pathParams: z.object({
      projectId: projectIdSchema,
    }),
    headers: z.object({
      authorization: z.string().min(1).optional(),
    }),
    body: addProjectThreadRequestSchema,
    responses: {
      201: addProjectThreadResponseSchema,
      401: errorSchema,
      403: errorSchema,
      404: errorSchema,
      409: errorSchema,
      422: errorSchema,
    },
    summary:
      'Pin an existing visible thread to this project. Idempotent — re-adding returns the existing membership.',
  },
  removeProjectThread: {
    method: 'DELETE',
    path: '/projects/:projectId/threads/:threadId',
    pathParams: z.object({
      projectId: projectIdSchema,
      threadId: z.string().min(1).max(64),
    }),
    headers: z.object({
      authorization: z.string().min(1).optional(),
    }),
    body: z.object({}).optional(),
    responses: {
      204: z.object({}),
      401: errorSchema,
      403: errorSchema,
      404: errorSchema,
    },
    summary: 'Remove a thread membership from a project',
  },
  listThreadProjects: {
    method: 'GET',
    path: '/threads/:threadId/projects',
    pathParams: z.object({
      threadId: z.string().min(1).max(64),
    }),
    headers: z.object({
      authorization: z.string().min(1).optional(),
    }),
    responses: {
      200: listThreadProjectsResponseSchema,
      401: errorSchema,
    },
    summary:
      'List the projects a thread belongs to that are visible to the viewer (org/author scope). Returns an empty list when there are none.',
  },
  deleteProject: {
    method: 'DELETE',
    path: '/projects/:projectId',
    pathParams: z.object({
      projectId: projectIdSchema,
    }),
    headers: z.object({
      authorization: z.string().min(1).optional(),
    }),
    body: z.object({}).optional(),
    responses: {
      204: z.object({}),
      401: errorSchema,
      403: errorSchema,
      404: errorSchema,
    },
    summary:
      'Permanently delete a project. Thread memberships and tasks are removed (FK cascade); child docs are detached (project_id set null).',
  },
});

export type ProjectStatus = z.infer<typeof projectStatusSchema>;
export type ProjectAuthor = z.infer<typeof projectAuthorSchema>;
export type ProjectThread = z.infer<typeof projectThreadSchema>;
export type ProjectListObject = z.infer<typeof projectListObjectSchema>;
export type ProjectListResponse = z.infer<typeof projectListResponseSchema>;
export type ListProjectsQuery = z.infer<typeof listProjectsQuerySchema>;
export type CreateProjectRequest = z.infer<typeof createProjectRequestSchema>;
export type CreateProjectResponse = z.infer<typeof createProjectResponseSchema>;
export type UpdateProjectRequest = z.infer<typeof updateProjectRequestSchema>;
export type UpdateProjectResponse = z.infer<typeof updateProjectResponseSchema>;
export type ArchiveProjectRequest = z.infer<typeof archiveProjectRequestSchema>;
export type ArchiveProjectResponse = z.infer<typeof archiveProjectResponseSchema>;
export type GetProjectResponse = z.infer<typeof getProjectResponseSchema>;
export type SynthesizeProjectResponse = z.infer<typeof synthesizeProjectResponseSchema>;
export type AddProjectThreadRequest = z.infer<typeof addProjectThreadRequestSchema>;
export type AddProjectThreadResponse = z.infer<typeof addProjectThreadResponseSchema>;
export type ThreadProject = z.infer<typeof threadProjectSchema>;
export type ListThreadProjectsResponse = z.infer<typeof listThreadProjectsResponseSchema>;
